import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Plus, 
  ArrowLeft, 
  RefreshCw,
  FileText,
  Trash2,
  Save,
  Check,
  X,
  Search,
  Receipt,
  ShoppingCart,
  Wallet,
  ArrowRightLeft,
  CreditCard,
  ArrowDownLeft,
  ArrowUpRight,
  RefreshCcw
} from 'lucide-react';
import { toast } from 'sonner';
import { api, getErrorMessage } from '../App';

const VOUCHER_TYPES = [
  { value: 'journal', label: 'Journal', icon: FileText, color: 'text-gray-600', bg: 'bg-gray-100' },
  { value: 'sales', label: 'Sales', icon: ArrowUpRight, color: 'text-green-600', bg: 'bg-green-100' },
  { value: 'purchase', label: 'Purchase', icon: ShoppingCart, color: 'text-red-600', bg: 'bg-red-100' },
  { value: 'receipt', label: 'Receipt', icon: ArrowDownLeft, color: 'text-blue-600', bg: 'bg-blue-100' },
  { value: 'payment', label: 'Payment', icon: CreditCard, color: 'text-orange-600', bg: 'bg-orange-100' },
  { value: 'contra', label: 'Contra', icon: ArrowRightLeft, color: 'text-purple-600', bg: 'bg-purple-100' },
  { value: 'credit_note', label: 'Credit Note', icon: ArrowDownLeft, color: 'text-cyan-600', bg: 'bg-cyan-100' },
  { value: 'debit_note', label: 'Debit Note', icon: ArrowUpRight, color: 'text-pink-600', bg: 'bg-pink-100' },
  { value: 'adjustment', label: 'Adjustment', icon: RefreshCw, color: 'text-amber-600', bg: 'bg-amber-100' },
  { value: 'expenditure', label: 'Expenditure', icon: Wallet, color: 'text-rose-600', bg: 'bg-rose-100' },
];

const JournalEntryPage = ({ user }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [entries, setEntries] = useState([]);
  const [ledgers, setLedgers] = useState([]);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [search, setSearch] = useState('');
  const [listTypeFilter, setListTypeFilter] = useState('all');
  
  const [formData, setFormData] = useState({
    voucher_type: 'journal',
    voucher_date: new Date().toISOString().split('T')[0],
    entries: [
      { ledger_id: '', debit: 0, credit: 0, narration: '' },
      { ledger_id: '', debit: 0, credit: 0, narration: '' }
    ],
    narration: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  // Refetch ledgers when tab becomes visible (e.g., after adding a ledger in another tab)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Only refetch ledgers, not all data to avoid losing form state
        api.get('/finance/ledgers').then(res => {
          setLedgers(res.data);
        }).catch(console.error);
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [entriesRes, ledgersRes] = await Promise.all([
        api.get('/finance/journal-entries'),
        api.get('/finance/ledgers')
      ]);
      setEntries(entriesRes.data);
      setLedgers(ledgersRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddLine = () => {
    setFormData({
      ...formData,
      entries: [...formData.entries, { ledger_id: '', debit: 0, credit: 0, narration: '' }]
    });
  };

  const handleRemoveLine = (index) => {
    if (formData.entries.length <= 2) {
      toast.error('Minimum 2 lines required');
      return;
    }
    const newEntries = formData.entries.filter((_, i) => i !== index);
    setFormData({ ...formData, entries: newEntries });
  };

  const handleLineChange = (index, field, value) => {
    const newEntries = [...formData.entries];
    newEntries[index] = { ...newEntries[index], [field]: value };
    
    // Auto-balance: if debit entered, clear credit and vice versa
    if (field === 'debit' && value > 0) {
      newEntries[index].credit = 0;
    } else if (field === 'credit' && value > 0) {
      newEntries[index].debit = 0;
    }
    
    setFormData({ ...formData, entries: newEntries });
  };

  const calculateTotals = () => {
    const totalDebit = formData.entries.reduce((sum, e) => sum + (parseFloat(e.debit) || 0), 0);
    const totalCredit = formData.entries.reduce((sum, e) => sum + (parseFloat(e.credit) || 0), 0);
    return { totalDebit, totalCredit, difference: Math.abs(totalDebit - totalCredit) };
  };

  const handleCreateEntry = async () => {
    const { totalDebit, totalCredit, difference } = calculateTotals();
    
    if (difference > 0.01) {
      toast.error(`Debit (${formatCurrency(totalDebit)}) and Credit (${formatCurrency(totalCredit)}) must be equal`);
      return;
    }
    
    // Validate all lines have ledger selected
    const invalidLines = formData.entries.filter(e => !e.ledger_id || (e.debit === 0 && e.credit === 0));
    if (invalidLines.length > 0) {
      toast.error('All lines must have a ledger and amount');
      return;
    }
    
    try {
      setSaving(true);
      const response = await api.post('/finance/journal-entries', formData);
      toast.success(`Journal entry ${response.data.voucher_number} created`);
      setShowCreateDialog(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to create entry'));
    } finally {
      setSaving(false);
    }
  };

  const handlePostEntry = async (entryId) => {
    try {
      await api.patch(`/finance/journal-entries/${entryId}/post`);
      toast.success('Entry posted to ledgers');
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to post entry'));
    }
  };

  const resetForm = () => {
    setFormData({
      voucher_type: 'journal',
      voucher_date: new Date().toISOString().split('T')[0],
      entries: [
        { ledger_id: '', debit: 0, credit: 0, narration: '' },
        { ledger_id: '', debit: 0, credit: 0, narration: '' }
      ],
      narration: ''
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const getVoucherTypeConfig = (type) => {
    return VOUCHER_TYPES.find(v => v.value === type) || VOUCHER_TYPES[0];
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'posted': return 'bg-green-100 text-green-700 border-green-200';
      case 'draft': return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      case 'cancelled': return 'bg-red-100 text-red-700 border-red-200';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const filteredEntries = entries.filter(entry => {
    const matchesSearch = !search || 
      entry.voucher_number?.toLowerCase().includes(search.toLowerCase()) ||
      entry.narration?.toLowerCase().includes(search.toLowerCase());
    const matchesType = listTypeFilter === 'all' || entry.voucher_type === listTypeFilter;
    return matchesSearch && matchesType;
  });

  const { totalDebit, totalCredit, difference } = calculateTotals();
  const currentVoucherType = getVoucherTypeConfig(formData.voucher_type);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/admin/finance')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Journal Entries</h1>
            <p className="text-gray-600">{entries.length} vouchers</p>
          </div>
        </div>
        <Button onClick={() => setShowCreateDialog(true)}>
          <Plus className="h-4 w-4 mr-2" />
          New Entry
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search vouchers..."
            className="pl-10"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <Select value={listTypeFilter} onValueChange={setListTypeFilter}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Filter by type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            {VOUCHER_TYPES.map(type => (
              <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Entries List */}
      <Card>
        <CardContent className="p-0">
          <div className="divide-y">
            {filteredEntries.length > 0 ? (
              filteredEntries.map((entry) => {
                const typeConfig = getVoucherTypeConfig(entry.voucher_type);
                const TypeIcon = typeConfig.icon;
                return (
                  <div 
                    key={entry.id}
                    className="p-4 hover:bg-gray-50 cursor-pointer"
                    onClick={() => setSelectedEntry(entry)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${typeConfig.bg}`}>
                          <TypeIcon className={`h-5 w-5 ${typeConfig.color}`} />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-gray-900">{entry.voucher_number}</span>
                            <Badge variant="outline" className="text-xs">
                              {typeConfig.label}
                            </Badge>
                            <Badge className={`text-xs ${getStatusColor(entry.status)}`}>
                              {entry.status}
                            </Badge>
                          </div>
                          <p className="text-sm text-gray-500">
                            {entry.voucher_date} • {entry.narration?.substring(0, 50)}
                            {entry.narration?.length > 50 ? '...' : ''}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="font-medium text-gray-900">{formatCurrency(entry.total_debit)}</p>
                          <p className="text-xs text-gray-500">{entry.entries?.length || 0} lines</p>
                        </div>
                        {entry.status === 'draft' && (
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={(e) => {
                              e.stopPropagation();
                              handlePostEntry(entry.id);
                            }}
                          >
                            <Check className="h-4 w-4 mr-1" />
                            Post
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="text-center py-12 text-gray-500">
                <FileText className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                <p>No journal entries found</p>
                <Button 
                  variant="link" 
                  className="mt-2"
                  onClick={() => setShowCreateDialog(true)}
                >
                  Create your first entry
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Create Entry Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Journal Entry</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Voucher Type Tabs */}
            <div>
              <Label className="text-sm font-medium mb-3 block">Entry Type</Label>
              <div className="flex flex-wrap gap-2">
                {VOUCHER_TYPES.map((type) => {
                  const TypeIcon = type.icon;
                  const isSelected = formData.voucher_type === type.value;
                  return (
                    <Button
                      key={type.value}
                      variant={isSelected ? "default" : "outline"}
                      className={`flex items-center gap-2 ${isSelected ? '' : 'hover:bg-gray-100'}`}
                      onClick={() => setFormData({ ...formData, voucher_type: type.value })}
                    >
                      <TypeIcon className={`h-4 w-4 ${isSelected ? 'text-white' : type.color}`} />
                      {type.label}
                    </Button>
                  );
                })}
              </div>
            </div>

            {/* Date and Narration */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="voucher_date">Date</Label>
                <Input
                  id="voucher_date"
                  type="date"
                  value={formData.voucher_date}
                  onChange={(e) => setFormData({...formData, voucher_date: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="narration">Narration</Label>
                <Input
                  id="narration"
                  value={formData.narration}
                  onChange={(e) => setFormData({...formData, narration: e.target.value})}
                  placeholder="Entry description"
                />
              </div>
            </div>

            {/* Entry Lines */}
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-sm font-medium text-gray-600">Ledger Account</th>
                    <th className="px-4 py-2 text-right text-sm font-medium text-gray-600 w-32">Debit (₹)</th>
                    <th className="px-4 py-2 text-right text-sm font-medium text-gray-600 w-32">Credit (₹)</th>
                    <th className="px-4 py-2 text-left text-sm font-medium text-gray-600 w-40">Narration</th>
                    <th className="px-4 py-2 w-10"></th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {formData.entries.map((entry, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-4 py-2">
                        <Select 
                          value={entry.ledger_id} 
                          onValueChange={(value) => handleLineChange(index, 'ledger_id', value)}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select ledger" />
                          </SelectTrigger>
                          <SelectContent>
                            {ledgers.map(ledger => (
                              <SelectItem key={ledger.id} value={ledger.id}>
                                {ledger.code} - {ledger.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="px-4 py-2">
                        <Input
                          type="number"
                          value={entry.debit || ''}
                          onChange={(e) => handleLineChange(index, 'debit', parseFloat(e.target.value) || 0)}
                          className="text-right"
                          placeholder="0"
                        />
                      </td>
                      <td className="px-4 py-2">
                        <Input
                          type="number"
                          value={entry.credit || ''}
                          onChange={(e) => handleLineChange(index, 'credit', parseFloat(e.target.value) || 0)}
                          className="text-right"
                          placeholder="0"
                        />
                      </td>
                      <td className="px-4 py-2">
                        <Input
                          value={entry.narration || ''}
                          onChange={(e) => handleLineChange(index, 'narration', e.target.value)}
                          placeholder="Optional"
                        />
                      </td>
                      <td className="px-4 py-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleRemoveLine(index)}
                          disabled={formData.entries.length <= 2}
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-gray-50 border-t-2">
                  <tr>
                    <td className="px-4 py-2 font-medium">Total</td>
                    <td className="px-4 py-2 text-right font-medium">{formatCurrency(totalDebit)}</td>
                    <td className="px-4 py-2 text-right font-medium">{formatCurrency(totalCredit)}</td>
                    <td colSpan="2" className="px-4 py-2">
                      {difference > 0.01 ? (
                        <span className="text-red-600 text-sm">
                          Difference: {formatCurrency(difference)}
                        </span>
                      ) : (
                        <span className="text-green-600 text-sm flex items-center gap-1">
                          <Check className="h-4 w-4" /> Balanced
                        </span>
                      )}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>

            <Button variant="outline" onClick={handleAddLine} className="w-full">
              <Plus className="h-4 w-4 mr-2" />
              Add Line
            </Button>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateEntry} disabled={saving || difference > 0.01}>
              {saving ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              Save Entry
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Entry Dialog */}
      <Dialog open={!!selectedEntry} onOpenChange={() => setSelectedEntry(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedEntry?.voucher_number}
              <Badge className={getStatusColor(selectedEntry?.status)}>
                {selectedEntry?.status}
              </Badge>
            </DialogTitle>
          </DialogHeader>
          
          {selectedEntry && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">Type</p>
                  <p className="font-medium">{getVoucherTypeConfig(selectedEntry.voucher_type).label}</p>
                </div>
                <div>
                  <p className="text-gray-500">Date</p>
                  <p className="font-medium">{selectedEntry.voucher_date}</p>
                </div>
                <div>
                  <p className="text-gray-500">Amount</p>
                  <p className="font-medium">{formatCurrency(selectedEntry.total_debit)}</p>
                </div>
              </div>

              {selectedEntry.narration && (
                <div>
                  <p className="text-gray-500 text-sm">Narration</p>
                  <p className="font-medium">{selectedEntry.narration}</p>
                </div>
              )}

              <div className="border rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-sm font-medium">Account</th>
                      <th className="px-4 py-2 text-right text-sm font-medium w-32">Debit</th>
                      <th className="px-4 py-2 text-right text-sm font-medium w-32">Credit</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {selectedEntry.entries?.map((entry, idx) => (
                      <tr key={idx}>
                        <td className="px-4 py-2">
                          <span className="text-gray-500 text-sm mr-2">{entry.ledger_code}</span>
                          {entry.ledger_name}
                        </td>
                        <td className="px-4 py-2 text-right">
                          {entry.debit > 0 ? formatCurrency(entry.debit) : '-'}
                        </td>
                        <td className="px-4 py-2 text-right">
                          {entry.credit > 0 ? formatCurrency(entry.credit) : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-gray-50">
                    <tr>
                      <td className="px-4 py-2 font-medium">Total</td>
                      <td className="px-4 py-2 text-right font-medium">{formatCurrency(selectedEntry.total_debit)}</td>
                      <td className="px-4 py-2 text-right font-medium">{formatCurrency(selectedEntry.total_credit)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              {selectedEntry.status === 'draft' && (
                <div className="flex justify-end">
                  <Button onClick={() => {
                    handlePostEntry(selectedEntry.id);
                    setSelectedEntry(null);
                  }}>
                    <Check className="h-4 w-4 mr-2" />
                    Post Entry
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default JournalEntryPage;
